export interface ApiEndpoint {
  id: string;
  method: string;
  path: string;
  title: string;
  description: string;

  /** Position (1-8) in the core Terrain Pro API workflow (Auth -> Validate -> Extract -> Run -> Download -> Map Layers -> Advanced Report). Rendered by the "API Demo Workflow" diagram. */
  step?: number;

  /** Position (1-6) in the Report Demo wizard (Select -> Figures -> Prerequisites -> Pile Binning -> Configure -> Generate). Rendered by the "Report Demo Workflow" diagram. Only set for endpoints that are part of that flow. */
  reportDemoStep?: number;

  /** Which Django app/service implements this endpoint. Defaults to 'terrain-pro-api' (the clean, documented REST API under /tools/api/v1/terrain-pro/) when omitted. */
  app?: 'terrain-pro-api' | 'terrain-pro-legacy' | 'vec-tiles' | 'gen1-plantpredict';

  /** Which demo page(s) in this docs site actually call this endpoint today. Omitted/empty means it is implemented and documented but not currently exercised by either demo. */
  usedBy?: Array<'api-demo' | 'report-demo'>;

  /** Extra context on how/why a demo page uses (or doesn't use) this endpoint. */
  usedByNote?: string;

  /** Whether a valid token is actually required for the request to succeed. Defaults to true. */
  authRequired?: boolean;

  /** Authorization header scheme the client must send. Defaults to 'Bearer'. Some legacy endpoints intentionally use a non-standard 'Bearer-PP' prefix; 'token-only' sends the raw token with no scheme prefix at all. */
  authScheme?: 'Bearer' | 'Bearer-PP' | 'token-only' | 'none';

  /** Caveats about how authentication is (or isn't) enforced server-side for this endpoint. */
  authNote?: string;

  /** Request content type. Defaults to 'application/json' (or multipart/form-data when a parameter has type 'file'). */
  contentType?: 'application/json' | 'multipart/form-data' | 'application/x-www-form-urlencoded';

  parameters?: Array<{
    name: string;
    type: string;
    required: boolean;
    description: string;
    /** Where this parameter is sent. Defaults to the request body for POST/PUT and the query string for GET. Set explicitly for a query-string parameter on a non-GET request (e.g. ?version=template on a POST). */
    in?: 'query' | 'body';
    /** Pre-filled value to use when generating the Postman collection request, instead of leaving it blank. */
    default?: string;
  }>;
  response: string;
}

export const apiEndpoints: ApiEndpoint[] = [
  // ==========================================================================
  // Step 1: Authentication
  // ==========================================================================
  {
    id: 'auth-info',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/auth-info',
    title: 'Get Authentication Info',
    description: 'Retrieve current user authentication information from JWT token. This endpoint validates your token and returns the authenticated user details.',
    step: 1,
    reportDemoStep: 1,
    usedBy: ['api-demo', 'report-demo'],
    usedByNote: 'Called by API Demo\'s Step 1 (Authenticate). Called by Report Demo\'s Step 1 (Authorize) via applyAuthToken() to validate the token and resolve the user email/Cognito UUID used later when generating a report.',
    parameters: [],
    response: `{
  "authenticated": true,
  "user": "user@example.com",
  "cognito_uuid": "abc123...",
  "message": "Authentication successful"
}`
  },

  {
    id: 'email-login',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/email-login',
    title: 'Email Login',
    description: 'Generate an access token from a user\'s email address. Looks up the user in the system, retrieves their Cognito app-client credentials, and exchanges them for an access token via the Cognito OAuth2 token endpoint. This endpoint does NOT require authentication.',
    step: 1,
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s "Email Address" quick-login row. Report Demo does not offer this shortcut — it expects a JWT to already be set (shared via the same AuthContext/localStorage as the other pages).',
    authRequired: false,
    authScheme: 'none',
    parameters: [
      {
        name: 'email',
        type: 'string',
        required: true,
        description: 'User email address'
      }
    ],
    response: `// Success (200)
{
  "token": "eyJraWQiOi...",
  "user": "user@example.com",
  "cognito_uuid": "09d412c7-..."
}

// Not Found (404)
{
  "exists": false,
  "error": "No account found for this email"
}`
  },

  // ==========================================================================
  // Step 2: File Validation
  // ==========================================================================
  {
    id: 'validate-file',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/validate',
    title: 'Validate File and Create Hierarchy',
    description: 'Upload and validate a terrain file (DXF, XLSX, CSV, or PVC format). Creates or updates the Project → Layout → Configuration hierarchy. Supports three scenarios: (1) creating a new project with project_name, (2) adding a layout to an existing project with project_id, or (3) adding a configuration to an existing layout with layout_id. When creating a new project, you may optionally provide latitude/longitude and the backend will store them with the project record. The original uploaded filename is automatically captured and stored for display in the analysis list. All records created through the API are automatically marked with is_api_triggered = true to distinguish them from records created by the Terrain Pro application.',
    step: 2,
    usedBy: ['api-demo'],
    usedByNote: 'API Demo uploads via XMLHttpRequest (not fetch) so it can report upload progress. Report Demo never uploads files — it only works with projects/layouts/configurations that already exist.',
    contentType: 'multipart/form-data',
    parameters: [
      {
        name: 'file',
        type: 'file',
        required: true,
        description: 'The terrain file to validate'
      },
      {
        name: 'file_type',
        type: 'string',
        required: true,
        description: 'File type: DXF, XLSX, CSV, or PVC'
      },
      {
        name: 'user_uuid',
        type: 'string',
        required: true,
        description: 'User Cognito UUID'
      },
      {
        name: 'user_email',
        type: 'string',
        required: true,
        description: 'User email address'
      },
      {
        name: 'project_name',
        type: 'string',
        required: false,
        description: 'Project name (required when creating a new project, i.e. no project_id)'
      },
      {
        name: 'project_id',
        type: 'integer',
        required: false,
        description: 'Existing project ID (use instead of project_name to add a layout under an existing project)'
      },
      {
        name: 'layout_name',
        type: 'string',
        required: false,
        description: 'Layout name (required for Scenarios 1 and 2 — creating a new project or adding a layout to an existing project; not needed when layout_id is provided)'
      },
      {
        name: 'layout_id',
        type: 'integer',
        required: false,
        description: 'Existing layout ID (use to add a configuration under an existing layout)'
      },
      {
        name: 'configuration_name',
        type: 'string',
        required: false,
        description: 'Configuration name (defaults to "Configuration 1" if not provided)'
      },
      {
        name: 'latitude',
        type: 'string',
        required: false,
        description: 'Latitude for new project location (takes precedence over file-derived value)'
      },
      {
        name: 'longitude',
        type: 'string',
        required: false,
        description: 'Longitude for new project location (takes precedence over file-derived value)'
      },
      {
        name: 'city',
        type: 'string',
        required: false,
        description: 'City name for new project (used in layout_info location)'
      },
      {
        name: 'state',
        type: 'string',
        required: false,
        description: 'State/province for new project (used in layout_info location)'
      },
      {
        name: 'webhook_url',
        type: 'string',
        required: false,
        description: 'URL for webhook notifications'
      }
    ],
    response: `// DXF
{
  "job_id": "uuid",
  "status": "completed",
  "file_type": "DXF",
  "file_path": "development/uuid",
  "process_validation": true,
  "source_unit": "SI",
  "process_result": {
    "units": { "valid": true, "value": "Meters" },
    "epsg": { "valid": true, "value": 25830, "description": "ETRS89 / UTM zone 30N" },
    "elevation": { "valid": true, "layers_count": 2 },
    "blocks": { "valid": true, "count": 1 }
  },
  "project_id": 123,
  "layout_id": 456,
  "configuration_id": 789,
  "data_endpoint": "/api/v1/terrain-pro/validate/{job_id}/data"
}

// CSV
{
  "job_id": "uuid",
  "status": "completed",
  "file_type": "CSV",
  "file_path": "development/uuid",
  "process_validation": true,
  "process_result": {
    "columns_count_valid": true,
    "headers_match": true,
    "is_headers_exist": true,
    "original_filename": "pile_data.csv"
  },
  "source_unit": "SI",
  "configuration_id": 789,
  "layout_id": 456,
  "project_id": 123,
  "data_endpoint": "/api/v1/terrain-pro/validate/{job_id}/data"
}`
  },

  {
    id: 'get-validation-data',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/validate/{job_id}/data',
    title: 'Get Validation Data',
    description: 'Retrieve validation data for a completed validation job. Returns file-type-specific data: DXF files return layer/block selection data needed for extraction; CSV files return column mapping data; XLSX files return sheet/area information.',
    step: 2,
    usedBy: ['api-demo'],
    parameters: [
      {
        name: 'job_id',
        type: 'string',
        required: true,
        description: 'Validation job UUID'
      }
    ],
    response: `// DXF
{
  "job_id": "uuid",
  "job_type": "validation",
  "file_type": "DXF",
  "status": "completed",
  "process_validation": true,
  "validation_data": {
    "drawingUnit": "Meters",
    "coordinateSystem": {
      "description": "WGS 84 / UTM zone 30N",
      "epsg": 25830
    },
    "elevationLayer": [
      { "color": 9, "layerName": "C-TOPO-MAJR" },
      { "color": 8, "layerName": "C-TOPO-MINR" }
    ],
    "nestedBlocks": [
      {
        "width": 2.38,
        "length": 69.70,
        "rackBlockName": "1P60_tracker",
        "nestedBlocks": [
          { "selected": false, "nestedBlockName": "heavy pier edge" },
          { "selected": false, "nestedBlockName": "Heavy Motor Pier" }
        ]
      }
    ],
    "latitude": 37.57,
    "longitude": -5.15,
    "filePath": "development/uuid"
  }
}

// CSV
{
  "job_id": "uuid",
  "job_type": "validation",
  "file_type": "CSV",
  "status": "completed",
  "process_validation": true,
  "validation_data": {
    "csv_records": {
      "Row 0": ["Column 1", "Column 2", "Column 3", "Column 4", "Column 5", "Column 6", "Column 7"],
      "Row 1": ["Pile ID", "Tracker ID", "Easting", "Northing", "Ground Elevation", "Pile Name", "Tracker Name"],
      "Row 2": ["8", "125A9", "352228.47", "4417287.39", "469.67", "Heavy Array Pier", "28_EXT"]
    },
    "mapping_data": {
      "Easting":             { "column_index": 3, "column_description": "Easting" },
      "Pile ID":             { "column_index": 1, "column_description": "Pile ID" },
      "Rack ID":             { "column_index": 2, "column_description": "Tracker ID" },
      "Northing":            { "column_index": 4, "column_description": "Northing" },
      "Pile name":           { "column_index": 6, "column_description": "Pile Name" },
      "Rack name":           { "column_index": 7, "column_description": "Tracker Name" },
      "PRW modifier":        { "column_index": "",  "column_description": "" },
      "Ground elevation":    { "column_index": 5, "column_description": "Ground Elevation" },
      "First row with data": { "column_index": 2, "column_description": "" }
    }
  }
}

// XLSX
{
  "job_id": "uuid",
  "job_type": "validation",
  "file_type": "XLSX",
  "status": "completed",
  "process_validation": true,
  "validation_data": {
    "information_area": [
      { "row": 1, "area_name": "Area 1" }
    ],
    "pv_module_info": {...},
    "rack_info": {...}
  }
}`
  },

  // ==========================================================================
  // Step 3: Data Extraction
  // ==========================================================================
  {
    id: 'extract-file',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/extraction',
    title: 'Extract File Data',
    description: 'Extract terrain data from a validated file. For DXF files, specify elevation layers and rack blocks to extract (or use satellite elevation instead). For CSV files, pass the mapping_data from the validation response. For XLSX files, pass checked_areas_rows. May complete synchronously or return a job_id for async polling.',
    step: 3,
    usedBy: ['api-demo'],
    parameters: [
      {
        name: 'job_id',
        type: 'string',
        required: true,
        description: 'Validation job UUID to extract from'
      },
      {
        name: 'file_type',
        type: 'string',
        required: true,
        description: 'File type: DXF, XLSX, CSV, or PVC'
      },
      {
        name: 'user_uuid',
        type: 'string',
        required: true,
        description: 'User Cognito UUID'
      },
      {
        name: 'user_email',
        type: 'string',
        required: true,
        description: 'User email address'
      },
      {
        name: 'layers',
        type: 'array',
        required: false,
        description: 'Elevation layer names to extract (required for DXF unless is_satellite_data is true)'
      },
      {
        name: 'checked_blocks',
        type: 'array',
        required: false,
        description: 'Rack block names to extract (DXF only — at least one of checked_blocks or checked_nested_blocks required for DXF)'
      },
      {
        name: 'checked_nested_blocks',
        type: 'array',
        required: false,
        description: 'Nested block names to extract (DXF only — at least one of checked_blocks or checked_nested_blocks required for DXF)'
      },
      {
        name: 'is_satellite_data',
        type: 'boolean',
        required: false,
        description: 'Use satellite elevation data instead of DXF elevation layers (default: false). When true, layers are optional.'
      },
      {
        name: 'satellite_data',
        type: 'object',
        required: false,
        description: 'Satellite dataset to use when is_satellite_data is true. Fields: rasterId, productName, datasetId, source, elevation, pixelSize, resolution, verticalDatum, endDate, sourceUrl, x, y. Comes from Search Satellite Data (or its PlantPredict fallback).'
      },
      {
        name: 'configuration_id',
        type: 'integer',
        required: false,
        description: 'Configuration ID (auto-detected from job if omitted)'
      },
      {
        name: 'file_path',
        type: 'string',
        required: false,
        description: 'S3 file path of the validated file (auto-detected from job if omitted)'
      },
      {
        name: 'unit',
        type: 'string',
        required: false,
        description: 'Unit system: SI or Imperial (auto-detected from validation if omitted)'
      },
      {
        name: 'epsg',
        type: 'integer',
        required: false,
        description: 'EPSG code. For DXF: auto-detected from validation_data.coordinateSystem.epsg if omitted. For CSV/XLSX: must be provided explicitly — coordinate system is not available in CSV/XLSX validation data.'
      },
      {
        name: 'algorithm_version',
        type: 'number',
        required: false,
        description: 'Algorithm version to use (recommended: 6.3; backend default if omitted: 6.0). API Demo always sends 6.3.'
      },
      {
        name: 'checked_areas_rows',
        type: 'array',
        required: false,
        description: 'Row indices from information_area (required for XLSX — use the row values from GET /validate/{job_id}/data validation_data.information_area)'
      },
      {
        name: 'mapping_data',
        type: 'object',
        required: false,
        description: 'Column mapping for CSV files — pass the mapping_data object from GET /validate/{job_id}/data directly. Each key maps to {column_index, column_description}.'
      },
      {
        name: 'filtering_values',
        type: 'object',
        required: false,
        description: 'Weeding/supplementing parameters: {default_weeding_distance, default_weeding_angle, default_supplementing_distance, default_mid_ord_distance}. API Demo always sends {10, 10, 100, 0.1}.'
      },
      {
        name: 'webhook_url',
        type: 'string',
        required: false,
        description: 'URL to receive webhook notification when extraction completes or fails'
      }
    ],
    response: `// Async start (poll /job/{job_id}/status until completed)
{
  "job_id": "uuid",
  "status": "in_progress",
  "file_type": "CSV",
  "configuration_id": 789
}

// Sync completion — CSV example (status = "completed")
{
  "job_id": "uuid",
  "status": "completed",
  "file_type": "CSV",
  "file_path": "development/uuid",
  "process_validation": true,
  "process_result": {
    "success": true,
    "tracker_count": 1182,
    "piles_count": 11500,
    "pitch": 5.5,
    "latitude": 39.899,
    "longitude": -124.718,
    "mercator_run_id": 104
  },
  "validation_job_id": "uuid",
  "source_unit": "SI",
  "configuration_id": 789,
  "data_endpoint": "/api/v1/terrain-pro/extraction/{job_id}/data"
}

// DXF adds: "dxf_details_id": 123`
  },

  {
    id: 'get-extraction-data',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/extraction/{job_id}/data',
    title: 'Get Extraction Data',
    description: 'Retrieve extraction results for a completed extraction job. Returns success status, pile/tracker counts, and other extracted metrics.',
    step: 3,
    usedBy: ['api-demo'],
    parameters: [
      {
        name: 'job_id',
        type: 'string',
        required: true,
        description: 'Extraction job UUID'
      }
    ],
    response: `{
  "job_id": "uuid",
  "job_type": "extraction",
  "file_type": "CSV",
  "status": "completed",
  "process_validation": true,
  "extraction_data": {
    "success": true,
    "piles_count": 11500,
    "dxf_details_id": 123
  }
}`
  },

  {
    id: 'satellite-search',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/satellite-search',
    title: 'Search Satellite Data',
    description: 'Search available satellite elevation datasets for a given location. Returns a list of datasets sorted by pixel size (highest resolution first). Use the returned dataset object as satellite_data in the extraction request when is_satellite_data is true. If this call fails or returns non-JSON, API Demo automatically falls back to the PlantPredict-backed Get Elevation Data For Points endpoint below.',
    step: 3,
    usedBy: ['api-demo'],
    parameters: [
      {
        name: 'latitude',
        type: 'number',
        required: true,
        description: 'Latitude of the project location'
      },
      {
        name: 'longitude',
        type: 'number',
        required: true,
        description: 'Longitude of the project location'
      },
      {
        name: 'epsg',
        type: 'string',
        required: false,
        description: 'EPSG code string (e.g. "25830")'
      },
      {
        name: 'drawing_unit',
        type: 'string',
        required: false,
        description: 'Drawing unit (default: "meters")'
      }
    ],
    response: `{
  "elevation_details": [
    {
      "rasterId": 1,
      "productName": "1-meter DEM (USGS 3DEP)",
      "datasetId": "USGSNed1",
      "source": "USGS",
      "elevation": 469.2,
      "pixelSize": 1.0,
      "resolution": 1.0,
      "verticalDatum": "NAVD88",
      "endDate": 1609459200,
      "sourceUrl": "https://...",
      "x": 37.57,
      "y": -5.15,
      "selected": false
    }
  ]
}`
  },

  {
    id: 'satellite-search-fallback-geo',
    method: 'POST',
    path: '/gen1/v1/get-elevation-data-for-points',
    title: 'Get Elevation Data For Points (Satellite Fallback)',
    description: 'Fallback satellite-elevation lookup proxied through the PlantPredict/gen1 GEO service, used only when Search Satellite Data returns a non-JSON body, a non-OK status, or throws. Returns the same elevation_details shape so the UI can render results identically regardless of which endpoint answered.',
    step: 3,
    app: 'gen1-plantpredict',
    usedBy: ['api-demo'],
    usedByNote: 'Silent fallback inside API Demo\'s handleSatelliteSearch — the user never explicitly picks this endpoint, it is only reached if the primary Search Satellite Data call fails.',
    authScheme: 'Bearer-PP',
    authNote: 'Uses the non-standard "Bearer-PP <token>" scheme (same convention the Report Demo report-generation endpoints use) rather than plain "Bearer". When testing manually in Postman, set this request\'s Auth tab to "No Auth" (or "Inherit" will layer the collection\'s Bearer auth on top of/over the manual header, producing a malformed Authorization value that crashes the server\'s auth middleware with a 500 instead of a clean auth error) and send only the Authorization: Bearer-PP <token> header shown above.',
    parameters: [
      {
        name: 'coordinates',
        type: 'array',
        required: true,
        description: 'Array of [longitude, latitude] pairs, e.g. [[-5.15, 37.57]]'
      },
      {
        name: 'drawing_unit',
        type: 'string',
        required: false,
        description: '"meters" or "feet", derived from the satellite search form\'s drawing unit'
      },
      {
        name: 'epsg_num',
        type: 'string',
        required: false,
        description: 'EPSG code string'
      },
      {
        name: 'is_lat_lon_coordinate',
        type: 'boolean',
        required: false,
        description: 'Always sent as true by API Demo'
      }
    ],
    response: `{
  "elevation_details": [
    {
      "rasterId": 1,
      "productName": "1-meter DEM (USGS 3DEP)",
      "datasetId": "USGSNed1",
      "source": "USGS",
      "elevation": 469.2,
      "pixelSize": 1.0,
      "x": 37.57,
      "y": -5.15
    }
  ]
}`
  },

  {
    id: 'geo-info',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/geo-info',
    title: 'Get Geo Info',
    description: 'Retrieve location, elevation, and timezone for given coordinates by proxying to the PlantPredict GEO API. All three lookups run concurrently and are returned together.',
    step: 3,
    usedBy: ['api-demo'],
    usedByNote: 'Debounced (600ms) reverse-geocode used while the user types latitude/longitude in the Step 2 "Create New Project" form, to auto-fill city/state/elevation/timezone.',
    parameters: [
      {
        name: 'lat',
        type: 'number',
        required: true,
        description: 'Latitude'
      },
      {
        name: 'lon',
        type: 'number',
        required: true,
        description: 'Longitude'
      }
    ],
    response: `{
  "location": { ... },
  "elevation": { ... },
  "timezone": { ... }
}`
  },

  {
    id: 'get-job-status',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/job/{job_id}/status',
    title: 'Get Job Status',
    description: 'Check the status of any processing job (validation, extraction, or solution). Poll this endpoint until status is "completed" or "failed". Returns job progress, hierarchy IDs (project/layout/configuration), and a job-type-specific process_result. Used across all job types.',
    step: 3,
    usedBy: ['api-demo'],
    usedByNote: 'Polled from three separate call sites in API Demo: single-job polling for validation/extraction (5s interval, 60-attempt/5-minute timeout), multi-job polling for default/single-run analysis (3s interval, no timeout), and per-configuration polling for batch runs (3s interval, no timeout).',
    parameters: [
      {
        name: 'job_id',
        type: 'string',
        required: true,
        description: 'Job UUID (returned from POST /validate, POST /extraction, or any run endpoint)'
      }
    ],
    response: `// Validation job (CSV example)
{
  "job_id": "uuid",
  "job_type": "validation",
  "status": "completed",
  "file_type": "CSV",
  "file_path": "development/uuid",
  "process_validation": true,
  "process_result": {
    "headers_match": true,
    "is_headers_exist": true,
    "original_filename": "pile_data.csv",
    "columns_count_valid": true
  },
  "progress": 100,
  "error_message": null,
  "created_at": "2026-06-17T05:45:04.580407+00:00",
  "updated_at": "2026-06-17T05:45:04.580421+00:00",
  "configuration_id": 789,
  "configuration_name": "Configuration 1",
  "layout_id": 456,
  "layout_name": "Demo Layout",
  "project_id": 123,
  "project_name": "My Project",
  "terrain_pro_project_id": 123,
  "terrain_pro_layout_id": 456
}

// Extraction job
{
  "job_id": "uuid",
  "job_type": "extraction",
  "status": "completed",
  "file_type": "CSV",
  "file_path": "development/uuid",
  "process_validation": true,
  "process_result": {
    "success": true,
    "tracker_count": 1182,
    "piles_count": 11500,
    "pitch": 5.5,
    "latitude": 39.899,
    "longitude": -124.718,
    "mercator_run_id": 104
  },
  "progress": 100,
  "error_message": null,
  "created_at": "2026-06-17T06:00:00.000000+00:00",
  "updated_at": "2026-06-17T06:00:10.000000+00:00",
  "configuration_id": 789,
  "configuration_name": "Configuration 1",
  "layout_id": 456,
  "layout_name": "Demo Layout",
  "project_id": 123,
  "project_name": "My Project",
  "terrain_pro_project_id": 123,
  "terrain_pro_layout_id": 456
}`
  },

  // ==========================================================================
  // Step 4: Run Analysis
  // ==========================================================================
  {
    id: 'create-configuration',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/layouts/{layout_id}/create_configuration',
    title: 'Create Configuration (Draft)',
    description: 'Create one or more terrain analysis configurations in draft state without immediately running them. Accepts either a single configuration object or an array of objects for batch creation. The reveal_window value is entered in display units (mm for SI layouts, inches for Imperial layouts) and is automatically converted to storage units (meters / feet) before saving. Use POST /configurations/{id}/run to execute a draft configuration.',
    step: 4,
    usedBy: [],
    usedByNote: 'Not currently called by either demo page — both use the combined create+run endpoints (Run Configuration, Single Run Analysis, Batch Run Analysis) instead. Available for API consumers who want to review or edit parameters before running.',
    parameters: [
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID (in URL path)'
      },
      {
        name: 'min_pile_reveal',
        type: 'number',
        required: true,
        description: 'Minimum pile reveal height'
      },
      {
        name: 'max_slope_north',
        type: 'number',
        required: true,
        description: 'Maximum allowable north slope'
      },
      {
        name: 'max_slope_south',
        type: 'number',
        required: true,
        description: 'Maximum allowable south slope'
      },
      {
        name: 'reveal_window',
        type: 'number',
        required: false,
        description: 'Reveal window value in display units (mm for SI, inches for Imperial). Converted to storage units automatically.'
      },
      {
        name: 'max_articulation_angle',
        type: 'number',
        required: false,
        description: 'Maximum articulation angle (0 = no articulation)'
      },
      {
        name: 'configuration_name',
        type: 'string',
        required: false,
        description: 'Configuration name (auto-generated if not provided)'
      },
      {
        name: 'user_uuid',
        type: 'string',
        required: false,
        description: 'User Cognito UUID'
      },
      {
        name: 'user_email',
        type: 'string',
        required: false,
        description: 'User email address'
      },
      {
        name: 'algorithm_version',
        type: 'number',
        required: false,
        description: 'Algorithm version (default: 6.3)'
      }
    ],
    response: `// Single configuration
{
  "status": "success",
  "configurations": [
    {
      "config_id": 101,
      "config_name": "Configuration 5",
      "parameters": {
        "min_pile_reveal": 2.0,
        "reveal_window": 0.1,
        "max_articulation_angle": 0.75,
        "max_slope_north": 8.5,
        "max_slope_south": 8.5
      },
      "is_active": false,
      "status": "draft",
      "algorithm_version": 6.3
    }
  ],
  "total_created": 1,
  "unit": "SI",
  "reveal_window_display_unit": "mm",
  "message": "Created 1 configuration(s) in draft state. Use POST /configurations/{id}/run to execute."
}

// Batch (array body) — same structure, multiple items in configurations[]`
  },

  {
    id: 'run-configuration',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/configurations/{config_id}/run',
    title: 'Run Configuration',
    description: 'Execute a draft configuration. Reads all parameters from the stored configuration, determines the correct solution chain based on parameter values (Sol1 existence, reveal_window, max_articulation_angle), and queues Celery jobs accordingly. Chain logic: no Sol1 + angle=0 → Chain 2 (Sol1+Sol2); no Sol1 + angle>0 → Chain 3 (Sol1+Sol3+Sol4); Sol1 exists + angle=0 → Chain 4 (Sol2); Sol1 exists + angle>0 → Chain 5 (Sol3+Sol4).',
    step: 4,
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s "Run Default" mode — runs the configuration that was auto-created by Validate File / Extract File without changing its parameters.',
    parameters: [
      {
        name: 'config_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID (in URL path)'
      },
      {
        name: 'user_uuid',
        type: 'string',
        required: false,
        description: 'User Cognito UUID (falls back to value stored on the configuration)'
      },
      {
        name: 'user_email',
        type: 'string',
        required: false,
        description: 'User email address (falls back to value stored on the configuration)'
      },
      {
        name: 'optimization_goal',
        type: 'string',
        required: false,
        description: '"earthwork" (default) or "pileReveal". Controls which grading objective the solver optimizes for; validated server-side and rejected with a 400 if any other value is sent.'
      }
    ],
    response: `{
  "status": "success",
  "configuration": {
    "config_id": 101,
    "config_name": "Configuration 5",
    "parameters": {
      "min_pile_reveal": 2.0,
      "max_slope_north": 8.5,
      "max_slope_south": 8.5,
      "reveal_window": 0.1,
      "max_articulation_angle": 0.75,
      "algorithm_version": 6.3,
      "user_uuid": "user-123",
      "user_email": "user@example.com"
    },
    "is_active": true,
    "status": "running"
  },
  "chain_to_trigger": 3,
  "sol1_exists": false,
  "celery_task_id": "celery-task-uuid",
  "jobs": [
    { "job_id": "uuid...", "job_type": "solution_one",   "status": "queued",  "progress": 0 },
    { "job_id": "uuid...", "job_type": "solution_three", "status": "pending", "progress": 0 },
    { "job_id": "uuid...", "job_type": "solution_four",  "status": "pending", "progress": 0 }
  ],
  "message": "Configuration is running with chain 3. 3 job(s) queued. Monitor via /job/{job_id}/status"
}`
  },

  {
    id: 'single-run',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/layouts/{layout_id}/run',
    title: 'Single Run Analysis',
    description: 'Create a single configuration with specific parameters and immediately run the appropriate solution chain. Determines which chain to trigger based on reveal_window and max_articulation_angle: (0,0) = Sol1 only; (>0,0) = Sol2; (0,>0) = Sol3; (>0,>0) = Sol3 + Sol4. The reveal_window is entered in display units (mm for SI, inches for Imperial) and converted automatically. Created configurations are marked with is_api_triggered = true.',
    step: 4,
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s "New Single Run" mode, including the tracker-vendor preset selector that pre-fills min_pile_reveal/reveal_window/slope values.',
    parameters: [
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID (in URL path)'
      },
      {
        name: 'min_pile_reveal',
        type: 'number',
        required: true,
        description: 'Minimum pile reveal height'
      },
      {
        name: 'max_slope_north',
        type: 'number',
        required: true,
        description: 'Maximum allowable north slope'
      },
      {
        name: 'max_slope_south',
        type: 'number',
        required: true,
        description: 'Maximum allowable south slope'
      },
      {
        name: 'reveal_window',
        type: 'number',
        required: true,
        description: 'Reveal window value in display units (mm for SI, inches for Imperial). Single value (not array).'
      },
      {
        name: 'max_articulation_angle',
        type: 'number',
        required: true,
        description: 'Max articulation angle (0 = Sol2 only, >0 = Sol3 + Sol4)'
      },
      {
        name: 'algorithm_version',
        type: 'number',
        required: false,
        description: 'Algorithm version (default: 6.3)'
      },
      {
        name: 'user_uuid',
        type: 'string',
        required: false,
        description: 'User Cognito UUID'
      },
      {
        name: 'user_email',
        type: 'string',
        required: false,
        description: 'User email address'
      },
      {
        name: 'optimization_goal',
        type: 'string',
        required: false,
        description: '"earthwork" (default) or "pileReveal". Same validation as Run Configuration.'
      }
    ],
    response: `{
  "status": "success",
  "sol1_status": "exists",
  "sol1_job_id": null,
  "configuration": {
    "config_id": 101,
    "config_name": "Configuration 5",
    "parameters": {
      "reveal_window": 0.1,
      "max_articulation_angle": 0.75,
      "min_pile_reveal": 2.0
    },
    "solution_type": "solution_four",
    "is_active": false,
    "status": "draft"
  },
  "jobs": [
    { "job_id": "uuid...", "job_type": "solution_three", "status": "queued",  "progress": 0 },
    { "job_id": "uuid...", "job_type": "solution_four",  "status": "pending", "progress": 0 }
  ],
  "unit": "SI",
  "reveal_window_display_unit": "mm",
  "message": "Configuration created with 2 job(s). Monitor via /job/{job_id}/status"
}`
  },

  {
    id: 'batch-run',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/layouts/{layout_id}/runs/batch',
    title: 'Batch Run Analysis',
    description: 'Create batch solution runs with parameter variations. Generates a Cartesian product of reveal windows and max articulation angles, creating multiple configurations and running them all in parallel. Includes automatic Sol1 baseline creation if needed. The reveal_windows values are entered in display units (mm for SI, inches for Imperial) and converted automatically. Created configurations are marked with is_api_triggered = true.',
    step: 4,
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s "Batch Run" mode. reveal_windows / max_articulation_angles are parsed from comma-separated text inputs into number arrays client-side.',
    parameters: [
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID (in URL path)'
      },
      {
        name: 'min_pile_reveal',
        type: 'number',
        required: true,
        description: 'Minimum pile reveal height'
      },
      {
        name: 'max_slope_north',
        type: 'number',
        required: true,
        description: 'Maximum allowable north slope'
      },
      {
        name: 'max_slope_south',
        type: 'number',
        required: true,
        description: 'Maximum allowable south slope'
      },
      {
        name: 'reveal_windows',
        type: 'array',
        required: true,
        description: 'Array of reveal window values in display units (mm for SI, inches for Imperial), e.g. [100, 200, 300]'
      },
      {
        name: 'max_articulation_angles',
        type: 'array',
        required: true,
        description: 'Array of max articulation angle values (e.g. [0, 0.75, 1.5])'
      },
      {
        name: 'algorithm_version',
        type: 'number',
        required: false,
        description: 'Algorithm version to use for all batch runs (default: 6.3)'
      },
      {
        name: 'user_uuid',
        type: 'string',
        required: false,
        description: 'User Cognito UUID'
      },
      {
        name: 'user_email',
        type: 'string',
        required: false,
        description: 'User email address'
      },
      {
        name: 'optimization_goal',
        type: 'string',
        required: false,
        description: '"earthwork" (default) or "pileReveal", applied to every configuration in the batch. Same validation as Run Configuration.'
      }
    ],
    response: `{
  "status": "success",
  "sol1_status": "queued",
  "sol1_job_id": "uuid...",
  "configurations": [
    {
      "config_id": 100,
      "parameters": { "reveal": 0, "angle": 0 },
      "jobs": [
        { "job_id": "uuid...", "job_type": "solution_one", "status": "queued", "progress": 0 }
      ]
    },
    {
      "config_id": 101,
      "parameters": { "reveal": 0.1, "angle": 0.75 },
      "jobs": [
        { "job_id": "uuid...", "job_type": "solution_three", "status": "waiting_for_sol1", "progress": 0 },
        { "job_id": "uuid...", "job_type": "solution_four",  "status": "pending",          "progress": 0 }
      ]
    }
  ],
  "total_configs": 10,
  "total_jobs": 14,
  "unit": "SI",
  "reveal_window_display_unit": "mm",
  "message": "Created 10 configurations with 14 jobs."
}`
  },

  {
    id: 'list-runs',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/layouts/{layout_id}/runs',
    title: 'List Layout Runs',
    description: 'List all solution runs for a layout, grouped by parameter combination. Useful for reviewing all configurations that have been created and run for a layout.',
    step: 4,
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s Resource Endpoints explorer, "Get Layout Detail + Runs" row.',
    parameters: [
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID (in URL path)'
      }
    ],
    response: `{
  "layout_id": 456,
  "runs": [
    {
      "config_id": 101,
      "config_name": "Configuration 1",
      "parameters": { "reveal": 0.1, "angle": 0.75 },
      "jobs": [
        { "job_id": "uuid...", "job_type": "solution_three", "status": "completed", "progress": 100 }
      ]
    }
  ]
}`
  },

  {
    id: 'get-run-detail',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/runs/{job_id}',
    title: 'Get Run Detail',
    description: 'Get detailed information about a specific solution run job, including parameters, results, earthwork data, and related jobs with the same parameter combination.',
    step: 4,
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s Resource Endpoints explorer, "Get Run Detail" row. Note the path has no trailing slash, unlike most other resource endpoints.',
    parameters: [
      {
        name: 'job_id',
        type: 'string',
        required: true,
        description: 'Solution run job UUID'
      }
    ],
    response: `{
  "job_id": "uuid",
  "job_type": "solution_three",
  "status": "completed",
  "progress": 100,
  "file_type": "CSV",
  "process_validation": true,
  "created_at": "2026-06-17T06:00:00.000000+00:00",
  "updated_at": "2026-06-17T06:05:30.000000+00:00",
  "error_message": null,
  "parameters": {
    "min_pile_reveal": 2.0,
    "reveal_window": 0.1,
    "max_articulation_angle": 0.75,
    "parameters_hash": "abc123..."
  },
  "results": { ... },
  "earthwork": { ... },
  "configuration_id": 101,
  "configuration_name": "Configuration 5",
  "layout_id": 456,
  "layout_name": "Demo Layout",
  "parent_job_id": "uuid...",
  "parent_job_type": "solution_one",
  "related_jobs": [
    { "job_id": "uuid...", "job_type": "solution_four", "status": "completed", "progress": 100, "created_at": "..." }
  ]
}`
  },

  // ==========================================================================
  // Step 5: Results Download
  // ==========================================================================
  {
    id: 'list-outputs',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/configurations/{config_id}/outputs',
    title: 'List Configuration Outputs',
    description: 'List all available output files generated for a completed configuration. Returns one entry per completed job (solution_one, solution_two, solution_three, solution_four, and their earthwork/geojson variants). Each entry includes a presigned S3 URL for direct download (1-hour expiration by default) when an S3 path is available, or falls back to the API download endpoint path.',
    step: 5,
    usedBy: ['api-demo'],
    usedByNote: 'Called for both single/default runs and, in a loop per configuration, for batch runs. Also available in the Resource Endpoints explorer ("List Outputs" row).',
    parameters: [
      {
        name: 'config_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID (in URL path)'
      }
    ],
    response: `{
  "status": "success",
  "configuration_id": 101,
  "configuration_name": "Configuration 5",
  "outputs": [
    {
      "job_id": "uuid",
      "output_type": "solution_one",
      "display_name": "Solution 1",
      "status": "completed",
      "s3_path": "path/to/solution_one.json",
      "created_at": "2026-06-17T06:00:00.000000+00:00",
      "updated_at": "2026-06-17T06:05:00.000000+00:00",
      "download_url": "https://s3.amazonaws.com/presigned-url...",
      "expires_in": 3600
    },
    {
      "job_id": "uuid",
      "output_type": "solution_one_earthwork",
      "display_name": "Solution 1 - Earthwork",
      "status": "completed",
      "s3_path": "path/to/solution_one_earthwork.json",
      "created_at": "2026-06-17T06:05:00.000000+00:00",
      "updated_at": "2026-06-17T06:05:10.000000+00:00",
      "download_url": "/tools/api/v1/terrain-pro/configurations/101/outputs/solution_one/earthwork"
    }
  ],
  "total_outputs": 6
}`
  },

  {
    id: 'download-output',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/configurations/{config_id}/outputs/{solution_type}/{output_type}',
    title: 'Download Configuration Output',
    description: 'Download a specific output file for a configuration. solution_type is one of: solution_one, solution_two, solution_three, solution_four. output_type is one of: json_data (main solution output), earthwork, geojson. By default returns a presigned S3 URL (set presigned=false for direct file content). Use the download_url values from List Configuration Outputs to construct this URL.',
    step: 5,
    usedBy: ['api-demo'],
    usedByNote: 'The demo UI opens the download_url returned by List Configuration Outputs directly (usually already a presigned S3 URL) rather than calling this endpoint from JavaScript. It is the API-level equivalent for consumers that need to force presigned=false or want to construct the URL themselves.',
    parameters: [
      {
        name: 'config_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID (in URL path)'
      },
      {
        name: 'solution_type',
        type: 'string',
        required: true,
        description: 'Solution type: solution_one, solution_two, solution_three, or solution_four (in URL path)'
      },
      {
        name: 'output_type',
        type: 'string',
        required: true,
        description: 'Output type: json_data, earthwork, or geojson (in URL path)'
      },
      {
        name: 'presigned',
        type: 'boolean',
        required: false,
        description: 'Return a presigned S3 URL (default: true). Set to false to stream file content directly.'
      },
      {
        name: 'expiration',
        type: 'integer',
        required: false,
        description: 'Presigned URL expiration in seconds (default: 3600)'
      }
    ],
    response: `// presigned=true (default)
{
  "status": "success",
  "job_id": "uuid",
  "configuration_id": 101,
  "solution_type": "solution_one",
  "output_type": "json_data",
  "filename": "solution_one.json",
  "content_type": "application/json",
  "s3_path": "path/to/solution_one.json",
  "download_url": "https://s3.amazonaws.com/presigned-url...",
  "expires_in": 3600,
  "file_size": 1024000,
  "created_at": "2026-06-17T06:05:00.000000+00:00"
}

// presigned=false — returns raw file content with
// Content-Type: application/json (or application/geo+json for geojson)
// Content-Disposition: attachment; filename="solution_one.json"`
  },

  // ==========================================================================
  // Step 6: Map Layers
  // ==========================================================================
  {
    id: 'create-layers',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/configurations/{config_id}/layers',
    title: 'Create Map Layers',
    description: 'Trigger creation of the deck.gl map layers for a completed configuration — surface texture PNGs (heatmap fixed/dynamic, cut/fill, original, finish), elevation heightmap PNGs, terrarium PNGs, and the elevation manifest. Reuses the existing Terrain Pro vector-tile pipeline (deck.gl texture Celery task + elevation Lambda). Returns 202 Accepted with the expected layer set; poll GET .../layers for progress and presigned download URLs. If the layout GeoJSON is not already cached it is regenerated from the configuration\'s pile-info output (same fallback as /tools/api/tiles). Prerequisites: (1) the analysis must have run so the layout pitch is set — the endpoint returns 409 if pitch is 0/unset; (2) for DXF layouts the elevation/ folder (manifest + terrarium + LERC tiles) must exist, which Step 3 extraction now produces.',
    step: 6,
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s optional "Create Map Layers" card, available once Step 5 has a configuration_id.',
    parameters: [
      {
        name: 'config_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID (in URL path)'
      }
    ],
    response: `{
  "status": "processing",
  "configuration_id": 101,
  "project_id": 5100,
  "layout_id": 8421,
  "expected_layers": [
    { "name": "Heatmap Fixed texture", "layer_type": "texture", "surface_mode": "heatmap_fixed" },
    { "name": "Original elevation heightmap", "layer_type": "elevation", "surface_mode": "original" },
    { "name": "Finish terrarium", "layer_type": "terrarium", "surface_mode": "finish" },
    { "name": "Elevation manifest", "layer_type": "manifest", "surface_mode": null }
  ],
  "total_layers": 10,
  "poll_url": "/tools/api/v1/terrain-pro/configurations/101/layers"
}`
  },

  {
    id: 'list-layers',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/configurations/{config_id}/layers',
    title: 'List Map Layers (Progress + Download)',
    description: 'Poll the status of map-layer creation for a configuration. Each expected layer reports status "completed" (with a presigned S3 download_url) or "pending", and an aggregate { total, completed, progress } is returned. Call repeatedly after POST .../layers until status is "completed" — the same presigned-URL approach used for downloading output results.',
    step: 6,
    usedBy: ['api-demo'],
    usedByNote: 'Polled every 4 seconds after Create Map Layers is triggered; also available directly in the Resource Endpoints explorer ("Get Map Layers" row).',
    parameters: [
      {
        name: 'config_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID (in URL path)'
      },
      {
        name: 'expiration',
        type: 'integer',
        required: false,
        description: 'Presigned URL expiration in seconds (default: 3600)'
      }
    ],
    response: `{
  "status": "processing",
  "configuration_id": 101,
  "total": 10,
  "completed": 4,
  "progress": 40,
  "layers": [
    {
      "name": "Heatmap Fixed texture",
      "layer_type": "texture",
      "surface_mode": "heatmap_fixed",
      "s3_key": "development/project/5100/layout/8421/terrain_101_heatmap_fixed.png",
      "status": "completed",
      "download_url": "https://s3.amazonaws.com/...",
      "expires_in": 3600
    },
    {
      "name": "Finish terrarium",
      "layer_type": "terrarium",
      "surface_mode": "finish",
      "s3_key": "development/project/5100/layout/8421/terrain_101_finish_elevation_terrarium.png",
      "status": "pending"
    }
  ]
}`
  },

  {
    id: 'pile-summary',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/configurations/{config_id}/pile-summary',
    title: 'Get Pile Summary & Rack Summary',
    description: 'Return the pile-binning analysis (Pile Summary) and a section-type rack summary for a completed configuration. Reuses the same pile-binning service the Terrain Pro UI uses, computed from the configuration\'s completed solution output in S3. pile_summary contains the dual analysis (imported vs. calculated bins per pile type, totals, and chart data); rack_summary aggregates the calculated bins by steel section type with a trailing Totals row. Returns 409 if the configuration has not completed a solution run yet (no output available).',
    step: 6,
    usedBy: [],
    usedByNote: 'Not currently exercised by either demo page. Report Demo performs the equivalent calculation itself via the legacy Get Pile Binning Data endpoint instead. Intended for external API consumers and the production Terrain Pro web app. See the PileBinningAnalysis data model.',
    parameters: [
      {
        name: 'config_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID (in URL path)'
      }
    ],
    response: `{
  "config_id": 101,
  "pile_summary": {
    "analysis": [
      {
        "imported_data": { "W6x12-A": [ { "section_type": "W6x12", "count": 120, "total_weight": 14400, "start": 1.2, "end": 1.8, "minimum_embedment": 1.26, "pile_length": 3.0, "percentage": 22.4, "unit": "ft" } ] },
        "calculated_data": { "W6x12-A": [ { "section_type": "W6x12", "count": 120, "total_weight": 14400 } ] }
      },
      { "imported_totals": { "total_weight": 64200, "total_count": 535 }, "calculated_totals": { "total_weight": 64200, "total_count": 535 } },
      { "imported_chart_data": [], "calculated_chart_data": [] }
    ],
    "file_name": "config_101.csv",
    "dataSource": "calculated",
    "unit": "ft",
    "layout_name": "Layout A / Configuration 5"
  },
  "rack_summary": [
    { "Section Type": "W6x10.4", "Count": 210, "Total weight (lbs)": 21840 },
    { "Section Type": "W6x12", "Count": 120, "Total weight (lbs)": 14400 },
    { "Section Type": "W6x15", "Count": 205, "Total weight (lbs)": 27960 },
    { "Section Type": "Totals", "Pile length": null, "Count": 535, "Total weight (lbs)": 64200 }
  ]
}

// Not completed yet (409)
{
  "error": "Configuration has no completed solution output yet. Run the configuration and wait for it to complete before requesting the pile summary.",
  "status": "draft"
}`
  },

  {
    id: 'wind-rack-summary',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/configurations/{config_id}/wind-rack-summary',
    title: 'Get Wind-Exposure Rack Summary',
    description: 'Return the wind-exposure rack summary for the configuration\'s layout. Reuses the app\'s wind-exposure summary service. Availability is gated by layout source: only DP-Layout / DP / DXF layouts (and non-GFT) produce a summary — other sources return isAvailable=false with an empty summary. The summary is cached per layout and built from tracker spacing data on first request.',
    step: 6,
    usedBy: [],
    usedByNote: 'Not currently exercised by either demo page. Intended for external API consumers and the production Terrain Pro web app.',
    parameters: [
      {
        name: 'config_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID (in URL path)'
      }
    ],
    response: `// Available (DP-Layout / DP / DXF, non-GFT)
{
  "config_id": 101,
  "layout_id": 8421,
  "isAvailable": true,
  "summary": [ { "rackBlockName": "1P60_tracker", "count": 88, "ns_spacing": 18.2, "ew_coverage": 4.0 } ],
  "inputs": { "maxNsSpacing": 18.2, "ewTolerance": 2, "ewCoverageTolerance": 4, "unit": "m" }
}

// Not available for this layout source (200)
{
  "config_id": 101,
  "layout_id": 8421,
  "isAvailable": false,
  "summary": [],
  "inputs": {}
}`
  },

  // ==========================================================================
  // Resource Management Endpoints
  // ==========================================================================
  {
    id: 'my-projects',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/my-projects',
    title: 'List User Projects (Lightweight)',
    description: 'Retrieve a lightweight list of projects belonging to the authenticated user. Returns only id and project_name for fast loading in dropdowns and selection UIs. Filtered by the current user\'s Cognito UUID — no pagination, returns all matching projects. Note: this is a bare JSON array, not the {count, next, previous, results} pagination envelope used by List All Projects.',
    usedBy: ['api-demo', 'report-demo'],
    reportDemoStep: 2,
    usedByNote: 'API Demo\'s Step 2 "Select Existing Project" dropdown. Report Demo\'s Step 1 project picker.',
    parameters: [],
    response: `[
  { "id": 5862, "project_name": "Salt_river" },
  { "id": 5843, "project_name": "Sur-972_559" },
  { "id": 5100, "project_name": "Demo Project" }
]`
  },

  {
    id: 'list-projects',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/projects/',
    title: 'List All Projects (Paginated)',
    description: 'Retrieve a paginated list of all projects scoped to the authenticated user. Supports limit/offset pagination. Results are ordered by created_date descending. For a lightweight dropdown list, prefer GET /my-projects.',
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s "Explore Resource Endpoints" bulk fetch.',
    parameters: [
      {
        name: 'limit',
        type: 'integer',
        required: false,
        description: 'Number of items per page'
      },
      {
        name: 'offset',
        type: 'integer',
        required: false,
        description: 'Number of items to skip'
      }
    ],
    response: `{
  "count": 50,
  "next": "https://domain/tools/api/v1/terrain-pro/projects/?limit=15&offset=15",
  "previous": null,
  "results": [
    {
      "id": 5862,
      "project_id": "ext-ref-id",
      "project_name": "Salt_river",
      "owner_email_id": "user@example.com",
      "latitude": 37.57,
      "longitude": -5.15,
      "city": "Seville",
      "state": "Andalusia",
      "layouts_count": 3,
      "created_date": "2026-06-17T05:45:00.000000+00:00"
    }
  ]
}`
  },

  {
    id: 'get-project',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/projects/{id}/',
    title: 'Get Project Details',
    description: 'Retrieve detailed information about a specific project, including all associated layouts.',
    usedBy: ['api-demo', 'report-demo'],
    reportDemoStep: 4,
    usedByNote: 'API Demo\'s Resource Endpoints explorer ("Get Project Detail" row). Report Demo\'s Step 3 "Check Prerequisites" calls this to fetch the project\'s epsg_code and backfill display fields (project name/location/coordinates) if they weren\'t already set.',
    parameters: [
      {
        name: 'id',
        type: 'integer',
        required: true,
        description: 'Project ID'
      }
    ],
    response: `{
  "id": 5862,
  "uuid": "abc-123",
  "project_id": "ext-ref-id",
  "project_name": "Salt_river",
  "owner_uuid": "cognito-uuid",
  "owner_email_id": "user@example.com",
  "latitude": 37.57,
  "longitude": -5.15,
  "city": "Seville",
  "state": "Andalusia",
  "epsg_code": 25830,
  "epsg_from_input": true,
  "elevation_source": "USGS",
  "elevation_resolution": 1.0,
  "created_date": "2026-06-17T05:45:00.000000+00:00",
  "layouts": [
    {
      "id": 456,
      "uuid": "def-456",
      "name": "Layout A",
      "source": "DXF",
      "source_unit": "SI",
      "terrainpro_project_id": 5862,
      "created_date": "2026-06-17T05:50:00.000000+00:00"
    }
  ]
}`
  },

  {
    id: 'list-layouts',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/layouts/',
    title: 'List Layouts',
    description: 'Retrieve a paginated list of all non-deleted layouts. Filter by project using ?project_id={id}. Results are ordered by created_date descending.',
    usedBy: ['api-demo', 'report-demo'],
    reportDemoStep: 2,
    usedByNote: 'API Demo\'s bulk resource explorer fetch. Report Demo\'s Step 1 layout picker, called with ?project_id={selectedProjectId} after a project is chosen.',
    parameters: [
      {
        name: 'limit',
        type: 'integer',
        required: false,
        description: 'Number of items per page'
      },
      {
        name: 'offset',
        type: 'integer',
        required: false,
        description: 'Number of items to skip'
      },
      {
        name: 'project_id',
        type: 'integer',
        required: false,
        description: 'Filter layouts by project ID'
      }
    ],
    response: `{
  "count": 25,
  "next": null,
  "previous": null,
  "results": [
    {
      "id": 456,
      "name": "Layout A",
      "source": "DXF",
      "terrainpro_project_id": 5862,
      "configurations_count": 5,
      "created_date": "2026-06-17T05:50:00.000000+00:00"
    }
  ]
}`
  },

  {
    id: 'get-layout',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/layouts/{id}/',
    title: 'Get Layout Details',
    description: 'Retrieve detailed information about a specific layout, including all associated configurations.',
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s Resource Endpoints explorer ("Get Layout Detail + Runs" row). Report Demo uses the legacy Get Layout Configurations (Legacy) endpoint instead, which returns a richer, UI-shaped payload.',
    parameters: [
      {
        name: 'id',
        type: 'integer',
        required: true,
        description: 'Layout ID'
      }
    ],
    response: `{
  "id": 456,
  "uuid": "def-456",
  "name": "Layout A",
  "source": "DXF",
  "dp_project_id": null,
  "pp_project_id": null,
  "terrainpro_project_id": 5862,
  "layout_id": null,
  "user_uuid": "cognito-uuid",
  "email_id": "user@example.com",
  "source_unit": "SI",
  "pitch": 5.5,
  "is_archived": false,
  "is_deleted": false,
  "created_date": "2026-06-17T05:50:00.000000+00:00",
  "configurations": [
    {
      "id": 789,
      "terrain_pro_layout_id": 456,
      "configuration_name": "Configuration 1",
      "min_pile_reveal": 2.0,
      "max_tt_axis_slope": null,
      "max_tt_axis_slope_north": 8.5,
      "max_tt_axis_slope_south": 8.5,
      "reveal_window": 0.1,
      "max_slope_change": 0.75,
      "status": "Completed",
      "algorithm_version": 6.3,
      "created_date": "2026-06-17T05:55:00.000000+00:00"
    }
  ]
}`
  },

  {
    id: 'list-configurations',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/configurations/',
    title: 'List Configurations',
    description: 'Retrieve a paginated list of all terrain analysis configurations. Filter by layout using ?layout_id={id}. Results are ordered by created_date descending.',
    usedBy: ['api-demo', 'report-demo'],
    reportDemoStep: 2,
    usedByNote: 'API Demo\'s bulk resource explorer fetch. Report Demo\'s Step 1 configuration picker, called with ?layout_id={selectedLayoutId} and then client-side filtered to status === "completed".',
    parameters: [
      {
        name: 'limit',
        type: 'integer',
        required: false,
        description: 'Number of items per page'
      },
      {
        name: 'offset',
        type: 'integer',
        required: false,
        description: 'Number of items to skip'
      },
      {
        name: 'layout_id',
        type: 'integer',
        required: false,
        description: 'Filter configurations by layout ID'
      }
    ],
    response: `{
  "count": 100,
  "next": null,
  "previous": null,
  "results": [
    {
      "id": 789,
      "configuration_name": "Configuration 1",
      "status": "Completed",
      "terrain_pro_layout_id": 456,
      "jobs_count": 4,
      "created_date": "2026-06-17T05:55:00.000000+00:00"
    }
  ]
}`
  },

  {
    id: 'get-configuration',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/configurations/{id}/',
    title: 'Get Configuration Details',
    description: 'Retrieve detailed information about a specific configuration, including all parameter values and associated API jobs.',
    usedBy: ['api-demo'],
    usedByNote: 'API Demo fetches this automatically as soon as Step 4 becomes active, to pre-populate the "Default Configuration" banner and enable the "Run Default" mode.',
    parameters: [
      {
        name: 'id',
        type: 'integer',
        required: true,
        description: 'Configuration ID'
      }
    ],
    response: `{
  "id": 789,
  "terrain_pro_layout_id": 456,
  "configuration_name": "Configuration 1",
  "min_pile_reveal": 2.0,
  "max_tt_axis_slope": null,
  "max_tt_axis_slope_north": 8.5,
  "max_tt_axis_slope_south": 8.5,
  "reveal_window": 0.1,
  "max_slope_change": 0.75,
  "is_active": true,
  "is_bookmarked": false,
  "is_completed": true,
  "error_message": null,
  "status": "Completed",
  "output_path": "path/to/output",
  "user_uuid": "cognito-uuid",
  "email_id": "user@example.com",
  "algorithm_version": 6.3,
  "slope_adjustment": null,
  "created_date": "2026-06-17T05:55:00.000000+00:00",
  "run_start_time": "2026-06-17T05:56:00.000000+00:00",
  "run_end_time": "2026-06-17T06:00:00.000000+00:00",
  "duration": "4 minutes",
  "jobs": [
    {
      "id": "uuid",
      "job_type": "solution_one",
      "file_type": "CSV",
      "status": "completed",
      "process_validation": true,
      "progress": 100,
      "error_message": null,
      "created_at": "2026-06-17T05:56:00.000000+00:00",
      "updated_at": "2026-06-17T06:00:00.000000+00:00"
    }
  ]
}`
  },

  {
    id: 'list-jobs',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/jobs/',
    title: 'List Jobs',
    description: 'Retrieve a paginated list of all processing jobs (validation, extraction, and solution jobs). Filter by configuration using ?configuration_id={id}.',
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s bulk resource explorer fetch.',
    parameters: [
      {
        name: 'limit',
        type: 'integer',
        required: false,
        description: 'Number of items per page'
      },
      {
        name: 'offset',
        type: 'integer',
        required: false,
        description: 'Number of items to skip'
      },
      {
        name: 'configuration_id',
        type: 'integer',
        required: false,
        description: 'Filter jobs by configuration ID'
      }
    ],
    response: `{
  "count": 200,
  "next": null,
  "previous": null,
  "results": [
    {
      "id": "uuid",
      "job_type": "solution_one",
      "file_type": "CSV",
      "file_path": "development/uuid",
      "status": "completed",
      "process_validation": true,
      "progress": 100,
      "error_message": null,
      "created_at": "2026-06-17T05:56:00.000000+00:00",
      "updated_at": "2026-06-17T06:00:00.000000+00:00"
    }
  ]
}`
  },

  {
    id: 'get-job-detail',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/jobs/{id}/',
    title: 'Get Job Detail',
    description: 'Retrieve full details of a specific processing job by its UUID, including the complete job_data payload with all input parameters and computed results.',
    usedBy: ['api-demo'],
    usedByNote: 'API Demo\'s Resource Endpoints explorer ("Get Job Detail" row).',
    parameters: [
      {
        name: 'id',
        type: 'string (UUID)',
        required: true,
        description: 'Job UUID (path parameter)'
      }
    ],
    response: `{
  "id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "job_type": "solution_one",
  "file_type": "CSV",
  "file_path": "development/a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "status": "completed",
  "process_validation": true,
  "process_result": {
    "units": { "valid": true, "value": "Meters" },
    "elevation": { "valid": true, "layers_count": 2 }
  },
  "progress": 100,
  "error_message": null,
  "user_uuid": "cognito-uuid",
  "user_email": "user@example.com",
  "webhook_url": null,
  "created_at": "2026-06-17T05:56:00.000000+00:00",
  "updated_at": "2026-06-17T06:00:00.000000+00:00",
  "configuration": 789,
  "terrain_pro_project": 101,
  "terrain_pro_layout": 456,
  "job_data": {
    "pile_count": 1500,
    "solution_results": { "sol1_complete": true }
  }
}`
  },

  // ==========================================================================
  // Report Demo — Step 1: legacy prefill endpoints
  // ==========================================================================
  {
    id: 'legacy-layout-configurations',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/reports/project/{project_id}/layout/{layout_id}/configurations',
    title: 'Get Layout Configurations (Legacy, proxied)',
    description: 'Legacy, UI-shaped equivalent of Get Layout Details — returns every configuration for a layout with the full set of display fields the Terrain Pro web app and Report Demo need (earthwork data, algorithm version display name, owner name, wind-exposure summary, unit/EPSG/location metadata for the layout itself, etc.) rather than the terse REST shape used by the clean API. Backed by the same underlying models as the terrain_pro_api app, just a different, older view — exposed here via report_configurations_detail_proxy (tools/terrain_pro_api/views.py), a thin pass-through to the original get_configurations_of_layout so it can be reached under this API\'s prefix.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 2,
    usedBy: ['report-demo'],
    usedByNote: 'Called when a layout is selected (to build the configuration detail cache) and again during report generation to re-resolve per-configuration parameters.',
    authNote: 'The original /tools/api/terrainpro/v1/... path is only enforced by the global JWT middleware (not DRF IsAuthenticated), and that middleware can\'t resolve a client_credentials token (the kind /email-login returns) outside the /tools/api/v1/terrain-pro/ prefix. This path is a proxy under that prefix specifically so Report Demo\'s token authenticates correctly — see the "Report Demo proxy endpoints" comment block in tools/terrain_pro_api/views.py.',
    parameters: [
      {
        name: 'project_id',
        type: 'integer',
        required: true,
        description: 'Project ID (in URL path)'
      },
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID (in URL path)'
      }
    ],
    response: `{
  "configurations": [
    {
      "id": 789,
      "configurationName": "Configuration 1",
      "minPileReveal": 2.0,
      "maxTTAxisSlope": null,
      "maxTTAxisSlopeNorth": 8.5,
      "maxTTAxisSlopeSouth": 8.5,
      "revealWindow": 0.1,
      "maxSlopeChange": 0.75,
      "isBookMarked": false,
      "isCompleted": true,
      "ownerEmail": "user@example.com",
      "createdDate": "2026-06-17T05:55:00.000000+00:00",
      "status": "Completed",
      "earthWorkData": { "volume": { "totalGrading": 12500 } },
      "sortingSequence": 1,
      "algorithmVersion": 6.3,
      "algorithmVersionNumber": "6.3",
      "ownerName": { "firstName": "Jane", "lastName": "Doe" },
      "min_max_legends_data": { ... },
      "count": 535,
      "slopeAdjustment": null,
      "importedGeotiffId": null,
      "optimizationGoal": "earthwork"
    }
  ],
  "layoutInfo": { ... },
  "layoutId": 456,
  "availableValues": { ... },
  "unitSelection": "SI",
  "projectId": 5862,
  "epsg_from_input": true,
  "lat": 37.57,
  "long": -5.15,
  "noEpsgLat": null,
  "noEpsgLon": null,
  "epsgCode": 25830,
  "source": "DXF",
  "projectName": "Salt_river",
  "sourceUnit": "SI",
  "projectCreatedDate": "2026-06-17T05:45:00.000000+00:00",
  "sourceLayoutId": null,
  "pitchValue": 5.5,
  "algorithmVersion": { "version": 6.3, "name": "v6.3" },
  "isPVCAvailable": false,
  "layer_details": { ... },
  "isWindExposureRackDataAvailable": true,
  "rackSummary": [ ... ],
  "windExposureInputs": { ... },
  "isSatelliteData": false
}`
  },

  {
    id: 'legacy-report-information',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/reports/project/{project_id}/layout/{layout_id}/report-information',
    title: 'Get Previous Report Information (Legacy, proxied)',
    description: 'Retrieve previously saved report metadata (project name/location/coordinate system, input file list, revision history) for a layout, so a repeat report generation doesn\'t require re-entering everything. Returns an empty object if no prior report metadata has been saved for this project/layout pair. Exposed here via report_information_proxy, a thin pass-through to the original get_report_information.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 2,
    usedBy: ['report-demo'],
    usedByNote: 'Called from confirmSelection() to pre-fill the Step 5 "Configure Report" form (input files, revisions, project name/location, coordinate system, lat/long).',
    authNote: 'The original /tools/api/terrainpro/v1/... path is only enforced by the global JWT middleware, which can\'t resolve a client_credentials token outside /tools/api/v1/terrain-pro/. Proxied under that prefix for the same reason as Get Layout Configurations above.',
    parameters: [
      {
        name: 'project_id',
        type: 'integer',
        required: true,
        description: 'Project ID (in URL path)'
      },
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID (in URL path)'
      }
    ],
    response: `// Found
{
  "terrainpro_project_id": 5862,
  "terrainpro_layout_id": 456,
  "input_info": [ { "filename": "site_survey.dxf", "format": "DXF", "dated": "2026-05-01" } ],
  "revision_info": [ { "rev": "A", "date": "2026-05-15", "description": "Initial issue", "preparedBy": "J. Doe", "checkedBy": "A. Smith", "approvedBy": "R. Lee" } ],
  "updated_by": "user@example.com",
  "updated_date": "2026-05-15T00:00:00.000000+00:00",
  "projectName": "Salt_river",
  "location": "Seville, Andalusia",
  "coordinateSystem": "WGS 84 / UTM zone 30N",
  "latitude": "37°34'12.0\\"N",
  "longitude": "5°9'0.0\\"W"
}

// Not found (200)
{}`
  },

  // ==========================================================================
  // Report Demo — Step 3: prerequisite checks (heatmap / pile-reveal / tiles)
  // ==========================================================================
  {
    id: 'legacy-heatmap-status',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/reports/project/{project_id}/layout/{layout_id}/heatmap-status',
    title: 'Get Heatmap & Pile-Reveal Status (Legacy, proxied)',
    description: 'For every configuration in a layout, check S3 (in parallel, via a thread pool) for the existence of heatmap tiles and pile-reveal PNG artifacts, and return the total grading volume from cached earthwork data. This is the polling endpoint that drives Report Demo\'s "Check Prerequisites" step — it does not itself generate anything, it only reports whether Create Tiles / Generate Pile Reveal have already produced their artifacts. Exposed here via heatmap_status_proxy, a thin pass-through to the original get_heatmap_status.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 4,
    usedBy: ['report-demo'],
    usedByNote: 'Polled every 5 seconds during Step 3 until every selected configuration has heatmapExists true and (pileRevealExists true or not applicable).',
    authNote: 'The original /tools/api/terrainpro/v1/... path is only enforced by the global JWT middleware, which can\'t resolve a client_credentials token outside /tools/api/v1/terrain-pro/. Proxied under that prefix for the same reason as Get Layout Configurations above.',
    parameters: [
      {
        name: 'project_id',
        type: 'integer',
        required: true,
        description: 'Project ID (in URL path)'
      },
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID (in URL path)'
      }
    ],
    response: `{
  "configurations": [
    { "id": 789, "name": "Configuration 1", "revealWindow": 0.1, "totalGrading": 12500, "heatmapExists": true, "pileRevealExists": false }
  ],
  "total": 1
}

// No configurations for this layout
{ "configurations": [], "message": "No configurations found for this layout" }`
  },

  {
    id: 'legacy-tiles',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/reports/tiles',
    title: 'Get/Create Layout Tiles & GeoJSON (proxied)',
    description: 'Fetch (or lazily generate) the layout\'s tracker/pile GeoJSON and its map bounds, used both to warm the map-bounds cache before generating a report and to trigger heatmap tile creation for a configuration. If a fresh cached copy of the layout\'s vector layers exists in the database it is returned immediately; otherwise data is pulled from Redis and layer creation is kicked off in the background, or as a last resort it is parsed fresh from the request. Returns a bounds object only when serving from the DB-cached path. Exposed here via tiles_proxy, a thin pass-through to the original get_geojson_data (tools/vec_tiles/views.py).',
    app: 'vec-tiles',
    reportDemoStep: 4,
    usedBy: ['report-demo'],
    usedByNote: 'Called twice: once (only if the heatmap already exists) purely to read cached map bounds for embedding in the report payload, and once per configuration whose heatmap is missing, to trigger heatmap/tile generation.',
    contentType: 'application/x-www-form-urlencoded',
    authNote: 'The original /tools/api/tiles path is only enforced by the global JWT middleware, which can\'t resolve a client_credentials token outside /tools/api/v1/terrain-pro/. Proxied under that prefix for the same reason as Get Layout Configurations above. Still uses request.POST (form-encoded), not a JSON body.',
    parameters: [
      {
        name: 'epsg',
        type: 'string',
        required: true,
        description: 'EPSG code for the layout'
      },
      {
        name: 'configuration_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID'
      },
      {
        name: 'pitch',
        type: 'string',
        required: false,
        description: 'Always sent as "0" by Report Demo'
      },
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID'
      },
      {
        name: 'project_id',
        type: 'integer',
        required: true,
        description: 'Project ID'
      },
      {
        name: 'socket_id',
        type: 'string',
        required: false,
        description: 'Correlation ID for the background job, e.g. "report_demo_bounds_{configId}" or "report_demo_heatmap_{configId}" — no actual WebSocket is used by this demo, it just polls Get Heatmap & Pile-Reveal Status.'
      }
    ],
    response: `// Served from cache (bounds available)
{
  "geojson": { "type": "FeatureCollection", "features": [ ... ] },
  "bounds": { "bounds": [[-5.20, 37.55], [-5.10, 37.60]] }
}

// Served from Redis / freshly generated (no bounds guaranteed)
{ "geojson": "<compressed or raw geojson payload>" }`
  },

  {
    id: 'legacy-generate-pile-reveal',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/reports/generate-pile-reveal',
    title: 'Generate Pile Reveal (Legacy, proxied)',
    description: 'Queue background generation of the pile-reveal heatmap PNG for a configuration (Celery task). Always returns 200 immediately with status "processing" once the configuration is validated to exist — it does not report synchronous completion (ready is always false in the response). Report Demo relies exclusively on Get Heatmap & Pile-Reveal Status\'s pileRevealExists flag to detect completion, not on this response. Exposed here via generate_pile_reveal_proxy, a thin pass-through to the original generate_pile_reveal.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 4,
    usedBy: ['report-demo'],
    usedByNote: 'Fired once per configuration whose pile-reveal artifact does not exist yet, during Step 3. A 404/405 response (configuration not found, or wrong method) is mapped to a client-side "not_available" state.',
    contentType: 'application/x-www-form-urlencoded',
    authNote: 'The original /tools/api/terrainpro/v1/... path is only enforced by the global JWT middleware, which can\'t resolve a client_credentials token outside /tools/api/v1/terrain-pro/. Proxied under that prefix for the same reason as Get Layout Configurations above.',
    parameters: [
      {
        name: 'configuration_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID'
      },
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID'
      },
      {
        name: 'project_id',
        type: 'integer',
        required: true,
        description: 'Project ID'
      },
      {
        name: 'socket_id',
        type: 'string',
        required: false,
        description: 'Correlation ID, e.g. "pile_reveal_layer_{configId}"'
      },
      {
        name: 'bounds',
        type: 'string',
        required: false,
        description: 'JSON-stringified bounds object; Report Demo always sends "{}"'
      }
    ],
    response: `{
  "success": true,
  "message": "Pile reveal generation queued",
  "configuration_id": 789,
  "task_id": "celery-task-uuid",
  "status": "processing",
  "ready": false,
  "s3_path": null,
  "processing_time": null
}

// Configuration not found (404)
{ "error": "Configuration 789 not found" }`
  },

  {
    id: 'legacy-epsg-data',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/reports/epsg-data',
    title: 'Get EPSG / CRS Name (Legacy, proxied)',
    description: 'Resolve a human-readable coordinate reference system name for an EPSG code, with an EPSG→ESRI fallback chain. Returns 404 if the code can\'t be resolved by either registry. Exposed here via epsg_data_proxy, a thin pass-through to the original epsg_data_view.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 4,
    usedBy: ['report-demo'],
    usedByNote: 'Called only when the coordinate system name is still unknown (coordSys is empty) while resolving report metadata.',
    authNote: 'The original /tools/api/terrainpro/v1/... path is only enforced by the global JWT middleware, which can\'t resolve a client_credentials token outside /tools/api/v1/terrain-pro/. Proxied under that prefix for the same reason as Get Layout Configurations above.',
    parameters: [
      {
        name: 'coord_ref_sys_code',
        type: 'string',
        required: true,
        description: 'EPSG code, e.g. "25830"'
      }
    ],
    response: `// Single match — unwrapped to a bare object
{ "coord_ref_sys_code": 25830, "coord_ref_sys_name": "ETRS89 / UTM zone 30N" }

// Not found (404)
{ "status": "no data found" }`
  },

  // ==========================================================================
  // Report Demo — Step 4: pile binning
  // ==========================================================================
  {
    id: 'legacy-pile-binning-data',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/reports/get-pile-binning-data',
    title: 'Get Pile Binning Data (Legacy, proxied)',
    description: 'Compute (or read from Redis cache) the dual pile-binning analysis for a configuration — imported vs. calculated bins per pile type, totals, and chart data — used to populate the interactive pile-binning table before report generation. The underlying view (process_pile_binning_file) is also registered at two other URLs: the legacy /tools/api/terrainpro/v1/get-pile-binning-data (auth-checked by the global middleware, but only under /tools/api/v1/terrain-pro/) and /tools/api/v1/pile-binning/analyze (which is in the JWT middleware bypass list and therefore NOT auth-checked at all). Report Demo now calls this proxied path. Exposed here via pile_binning_data_proxy.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 5,
    usedBy: ['report-demo'],
    usedByNote: 'Auto-triggered once per figure-selected configuration when Step 4 becomes active.',
    authNote: 'Proxied under /tools/api/v1/terrain-pro/ so the global JWT middleware can resolve a client_credentials token into a user — the legacy /tools/api/terrainpro/v1/... path could not. The unrelated /tools/api/v1/pile-binning/analyze URL (same view) is still in the middleware bypass list and remains unauthenticated — see the Data Models reference for the shared PileBinningAnalysis response shape.',
    parameters: [
      {
        name: 'configId',
        type: 'string',
        required: true,
        description: 'Configuration ID, stringified'
      },
      {
        name: 'guid',
        type: 'string',
        required: true,
        description: 'Client-generated correlation ID for this binning session'
      },
      {
        name: 'connection_id',
        type: 'string',
        required: false,
        description: 'Always sent as "0" by Report Demo (no real WebSocket connection)'
      },
      {
        name: 'binsmap',
        type: 'object',
        required: false,
        description: 'Always sent as null on the initial load request'
      },
      {
        name: 'unit',
        type: 'string',
        required: false,
        description: 'Always sent as "ft" by Report Demo'
      },
      {
        name: 'reqSource',
        type: 'string',
        required: false,
        description: 'Always sent as "TP"'
      },
      {
        name: 'file_name',
        type: 'string',
        required: false,
        description: 'e.g. "Terrain_pro_{configId}"'
      }
    ],
    response: `{
  "analysis": [
    {
      "imported_data": { "W6x12-A": [ { "start": 1.2, "end": 1.8, "count": 120, "percentage": 22.4, "section_type": "W6x12", "minimum_embedment": 1.26, "pile_length": 3.0, "total_weight": 14400 } ] },
      "calculated_data": { "W6x12-A": [ { "start": 1.2, "end": 1.8, "count": 120, "percentage": 22.4 } ] }
    },
    { "imported_totals": { "total_weight": 64200, "total_count": 535 }, "calculated_totals": { "total_weight": 64200, "total_count": 535 } },
    { "imported_chart_data": [], "calculated_chart_data": [] }
  ],
  "file_name": "Terrain_pro_789",
  "dataSource": "calculated",
  "unit": "ft",
  "layout_name": "Layout A / Configuration 1"
}`
  },

  {
    id: 'legacy-pile-binning-auto-save',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/reports/pile-binning/auto_save',
    title: 'Auto-Save Pile Binning Edits (Legacy, proxied)',
    description: 'Persist the current bin edits (colors, splits, deletes, category/label/embedment changes) for a configuration back to the pile-binning cache, so they survive a page reload. Fired on a 1-second debounce after every edit in the Step 4 pile-binning table. Exposed here via pile_binning_auto_save_proxy, a thin pass-through to the original auto_save.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 5,
    usedBy: ['report-demo'],
    authNote: 'The original /tools/api/v1/pile-binning/auto_save path is in the JWT middleware\'s bypass_paths list — the Authorization header was never validated there, so anyone who could reach that URL could auto-save arbitrary binning data for any config_id. This proxied path under /tools/api/v1/terrain-pro/ is NOT in the bypass list, so the middleware now actually resolves and requires a valid token — a genuine auth fix, not just a routing change.',
    parameters: [
      {
        name: 'config_id',
        type: 'string',
        required: true,
        description: 'Configuration ID, stringified'
      },
      {
        name: 'guid',
        type: 'string',
        required: true,
        description: 'Correlation ID from the initial Get Pile Binning Data response'
      },
      {
        name: 'data',
        type: 'object',
        required: true,
        description: '{ binsmap, totalvalues, chartdata, source: "imported" }'
      },
      {
        name: 'data_source',
        type: 'string',
        required: false,
        description: 'Always sent as "imported"'
      }
    ],
    response: `// Result of PileBinningService.auto_save_dual(); shape passed through as-is.
// Not fully consumed by Report Demo beyond logging — treat as opaque {status, ...} on success.
{ "status": "success" }`
  },

  {
    id: 'legacy-pile-binning-split-range',
    method: 'POST',
    path: '/tools/api/v1/pile-binning/split_range',
    title: 'Split Pile Bin Range (Legacy)',
    description: 'Split one pile-reveal bin into two at a given range value, recomputing counts/percentages for both halves. For dual-format (imported+calculated) configurations, returns the updated analysis in the same 3-element array shape as Get Pile Binning Data. For legacy single-format configurations, returns a bare 2-element [newBinRows, newChartRows] array instead — the frontend branches on the presence of an analysis key to tell which shape it got.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 5,
    usedBy: ['report-demo'],
    authRequired: false,
    authNote: 'This path is in the JWT middleware\'s bypass_paths list — not auth-checked.',
    parameters: [
      {
        name: 'pile_name',
        type: 'string',
        required: true,
        description: 'Pile/section type key being split'
      },
      {
        name: 'range_value',
        type: 'string',
        required: true,
        description: 'Split point, stringified number'
      },
      {
        name: 'reveal_range',
        type: 'array',
        required: true,
        description: '[start, end] of the bin being split'
      },
      {
        name: 'color',
        type: 'string',
        required: false,
        description: 'Bin color'
      },
      {
        name: 'symbol',
        type: 'string',
        required: false,
        description: 'Always sent as "basic-circle"'
      },
      {
        name: 'category',
        type: 'string',
        required: false,
        description: 'Bin category label'
      },
      {
        name: 'label',
        type: 'string',
        required: false,
        description: 'Bin display label'
      },
      {
        name: 'section_type',
        type: 'string',
        required: false,
        description: 'Steel section type'
      },
      {
        name: 'minimum_embedment',
        type: 'number',
        required: false,
        description: 'Minimum embedment depth'
      },
      {
        name: 'guid',
        type: 'string',
        required: true,
        description: 'Binning session correlation ID'
      },
      {
        name: 'config_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID'
      },
      {
        name: 'tpSource',
        type: 'string',
        required: false,
        description: 'Layout source, from the Get Pile Binning Data response\'s "source" field'
      },
      {
        name: 'unit',
        type: 'string',
        required: false,
        description: 'Unit system, e.g. "ft"'
      },
      {
        name: 'data_source',
        type: 'string',
        required: false,
        description: 'Always sent as "imported"'
      },
      {
        name: 'reveal_range_value',
        type: 'number',
        required: false,
        description: 'Always sent as 0.250'
      }
    ],
    response: `// Dual-format configuration
{
  "analysis": [
    { "imported_data": [ ... new bin rows ... ], "calculated_data": [ ... ] },
    { "imported_totals": { ... }, "calculated_totals": { ... } },
    { "imported_chart_data": [ ... ], "calculated_chart_data": [ ... ] }
  ],
  "dataSource": "calculated"
}

// Legacy single-format configuration — bare 2-element array
[ [ /* new bin rows */ ], [ /* new chart rows */ ] ]`
  },

  {
    id: 'legacy-pile-binning-delete-range',
    method: 'POST',
    path: '/tools/api/v1/pile-binning/delete_range',
    title: 'Delete Pile Bin Range (Legacy)',
    description: 'Delete a pile-reveal bin, merging its piles into an adjacent bin. The UI prevents deleting bin 0 or the last remaining bin. For dual-format configurations, the legacy-shaped [rows, chart] pair is nested under a legacy_result key alongside analysis (a different nesting convention than Split Pile Bin Range uses).',
    app: 'terrain-pro-legacy',
    reportDemoStep: 5,
    usedBy: ['report-demo'],
    authRequired: false,
    authNote: 'This path is in the JWT middleware\'s bypass_paths list — not auth-checked.',
    parameters: [
      {
        name: 'guid',
        type: 'string',
        required: true,
        description: 'Binning session correlation ID'
      },
      {
        name: 'pile_name',
        type: 'string',
        required: true,
        description: 'Pile/section type key'
      },
      {
        name: 'pile_list',
        type: 'array',
        required: true,
        description: 'Piles belonging to the bin being deleted'
      },
      {
        name: 'config_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID'
      },
      {
        name: 'tpSource',
        type: 'string',
        required: false,
        description: 'Layout source'
      },
      {
        name: 'unit',
        type: 'string',
        required: false,
        description: 'Unit system'
      },
      {
        name: 'current_bin_index',
        type: 'integer',
        required: true,
        description: 'Index of the bin being deleted'
      },
      {
        name: 'reveal_range_value',
        type: 'number',
        required: false,
        description: 'Always sent as 0.250'
      },
      {
        name: 'data_source',
        type: 'string',
        required: false,
        description: 'Always sent as "imported"'
      }
    ],
    response: `// Dual-format configuration
{
  "analysis": [ ... ],
  "legacy_result": [ [ /* new bin rows */ ], [ /* new chart rows */ ] ]
}

// Legacy single-format configuration — bare 2-element array
[ [ /* new bin rows */ ], [ /* new chart rows */ ] ]`
  },

  {
    id: 'legacy-pile-binning-calculation',
    method: 'POST',
    path: '/tools/api/v1/pile-binning/pile_binning_calculation',
    title: 'Recalculate Pile Length & Weight (Legacy)',
    description: 'Given an updated minimum embedment, bin end value, unit, section type, and pile count, recompute pile_length and total_weight for that bin. Called after a section-type change, an embedment-field blur, or a "reset embedment" action.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 5,
    usedBy: ['report-demo'],
    authRequired: false,
    authNote: 'This path is in the JWT middleware\'s bypass_paths list — not auth-checked.',
    parameters: [
      {
        name: 'minimum_embedment',
        type: 'number',
        required: true,
        description: 'Updated minimum embedment depth'
      },
      {
        name: 'end',
        type: 'number',
        required: true,
        description: 'Bin end value (reveal range upper bound)'
      },
      {
        name: 'unit',
        type: 'string',
        required: true,
        description: 'Unit system, e.g. "ft"'
      },
      {
        name: 'section_type',
        type: 'string',
        required: true,
        description: 'Steel section type, e.g. "W6x12"'
      },
      {
        name: 'count',
        type: 'integer',
        required: true,
        description: 'Pile count in the bin'
      },
      {
        name: 'config_id',
        type: 'integer',
        required: true,
        description: 'Configuration ID'
      }
    ],
    response: `// Backend returns a (pile_length, total_weight) tuple, which json.dumps
// serializes as a bare 2-element array:
[ 3.0, 14400 ]`
  },

  // ==========================================================================
  // Report Demo — Step 6: report generation (legacy DOCX pipeline)
  // ==========================================================================
  {
    id: 'legacy-generate-report-docx',
    method: 'POST',
    path: '/tools/api/terrainpro/v1/project/{project_id}/layout/{layout_id}/download-nextracker-advanced-report-docx',
    title: 'Generate Advanced Report (DOCX, Legacy)',
    description: 'Queue generation of the Nextracker Advanced Report DOCX for a layout. Adds projectId/layoutId to the payload server-side and resolves email via the Cognito token (overriding whatever the client sent, unless resolution fails). The version query parameter controls which template engine runs: "template" calls the modern generate_nextracker_docx_template_task Celery task; omitting it defaults to the older "legacy" renderer. This is the same underlying Celery task as the clean API\'s Generate Nextracker Advanced Report endpoint below — the two exist because this docs SPA has no browser canvas to screenshot a location map, unlike the production webapp, so a lat/lon fallback path (see Data Models) was added for it.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 7,
    usedBy: [],
    usedByNote: 'No longer called by Report Demo — superseded by the clean API\'s Generate Nextracker Advanced Report endpoint below, which queues the identical Celery task but resolves projectId/layoutId/email server-side from the URL and authenticated user instead of requiring them in the body, so it needs no legacy-prefix auth workaround. Kept here for reference / other callers still on the old path.',
    authScheme: 'Bearer-PP',
    authNote: 'Deliberately sent with the non-standard "Bearer-PP <token>" prefix. terabase.middleware.JWTAuth explicitly no-ops on this scheme and defers to JWTAuthenticationMiddleware, which still validates the token by splitting on the first space — so auth IS enforced, just via the middleware rather than DRF\'s authentication class. When testing manually in Postman, set this request\'s Auth tab to "No Auth" before sending — leaving it set to "Inherit" stacks the collection\'s Bearer auth on top of the manual Bearer-PP header, which can produce a malformed Authorization value and crash the middleware\'s split(" ") with an unhandled 500 instead of a normal auth error.',
    contentType: 'application/json',
    parameters: [
      {
        name: 'project_id',
        type: 'integer',
        required: true,
        description: 'Project ID (in URL path)'
      },
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID (in URL path)'
      },
      {
        name: 'version',
        type: 'string',
        required: false,
        description: 'Query parameter. "template" (always sent by Report Demo) or "legacy" (default if omitted — returns the file inline as base64 instead of an S3 url, use "template" unless you specifically need that)',
        in: 'query',
        default: 'template'
      },
      {
        name: 'projectName',
        type: 'string',
        required: false,
        description: 'Display project name'
      },
      {
        name: 'location',
        type: 'string',
        required: false,
        description: 'Display location string'
      },
      {
        name: 'latitude',
        type: 'string',
        required: false,
        description: 'Display-formatted (DMS) latitude'
      },
      {
        name: 'longitude',
        type: 'string',
        required: false,
        description: 'Display-formatted (DMS) longitude'
      },
      {
        name: 'lat',
        type: 'number',
        required: false,
        description: 'Raw decimal latitude, used as a satellite-thumbnail fallback for the report\'s location map (see the Clean API generate endpoint for details)'
      },
      {
        name: 'lon',
        type: 'number',
        required: false,
        description: 'Raw decimal longitude (see lat)'
      },
      {
        name: 'layoutName',
        type: 'string',
        required: false,
        description: 'Display layout name'
      },
      {
        name: 'coordinateSystem',
        type: 'string',
        required: false,
        description: 'Coordinate system name'
      },
      {
        name: 'epsgCode',
        type: 'string',
        required: false,
        description: 'EPSG code'
      },
      {
        name: 'filteredConfigIds',
        type: 'array',
        required: true,
        description: 'Configuration IDs selected in Step 2 (Select Figures)'
      },
      {
        name: 'selectedConfigDetails',
        type: 'array',
        required: true,
        description: 'Enriched configuration objects (from Get Layout Configurations (Legacy)), with earthWorkData converted to display units'
      },
      {
        name: 'units',
        type: 'string',
        required: false,
        description: 'Selected display unit system'
      },
      {
        name: 'sourceUnit',
        type: 'string',
        required: false,
        description: 'Layout\'s native storage unit system'
      },
      {
        name: 'arrayAreaUnit',
        type: 'string',
        required: false,
        description: '"ha" or "ac"'
      },
      {
        name: 'arrayArea',
        type: 'number',
        required: false,
        description: 'Total array area in arrayAreaUnit'
      },
      {
        name: 'email',
        type: 'string',
        required: false,
        description: 'Overridden server-side from the authenticated user unless resolution fails'
      },
      {
        name: 'bounds',
        type: 'object',
        required: false,
        description: 'Map bounds from Get/Create Layout Tiles & GeoJSON, or {} if not cached'
      },
      {
        name: 'PileRevealLegendRanges',
        type: 'array',
        required: false,
        description: 'Five {start, end} legend range buckets for the pile-reveal heatmap legend'
      },
      {
        name: 'socket_key',
        type: 'string',
        required: false,
        description: 'e.g. "report_demo_{layoutId}" — informational only, no WebSocket is used'
      },
      {
        name: 'revisionDetails',
        type: 'array',
        required: false,
        description: 'Revision history rows'
      },
      {
        name: 'inputFiles',
        type: 'array',
        required: false,
        description: 'Input file metadata rows'
      }
    ],
    response: `{
  "task_id": "celery-task-uuid",
  "status": "QUEUED",
  "version": "template"
}`
  },

  {
    id: 'legacy-docx-task-status',
    method: 'GET',
    path: '/tools/api/terrainpro/v1/docx-task-status/{task_id}/',
    title: 'Check DOCX Task Status (Legacy)',
    description: 'Poll the status of a report-generation Celery task started via Generate Advanced Report (DOCX, Legacy). Wraps celery.result.AsyncResult directly, same pattern as the clean API\'s status endpoint below. There is no separate "download report" endpoint: once status is "SUCCESS", result.url in this response IS the report file — a presigned S3 URL you can open directly in a browser or GET with a fresh, unauthenticated request. Do not attach this collection\'s Bearer or Bearer-PP auth header to that URL; S3 presigned URLs carry their own signature in the query string, and an extra Authorization header can trigger a SignatureDoesNotMatch error. In Postman, paste result.url into a new request with Auth set to "No Auth" and use "Send and Download" to save the .docx file.',
    app: 'terrain-pro-legacy',
    reportDemoStep: 7,
    usedBy: [],
    usedByNote: 'No longer called by Report Demo — superseded by the clean API\'s Check Nextracker Advanced Report Status endpoint below. Kept here for reference / other callers still on the old path.',
    authScheme: 'Bearer-PP',
    authNote: 'Same "Bearer-PP" scheme as the generate endpoint — enforced by the JWT middleware, not DRF. If you get an unhandled 500 ("Internal Server Error") when calling this from Postman rather than a clean JSON error, check that this request\'s Auth tab is set to "No Auth" and not "Inherit auth from parent" — an inherited Bearer auth stacked on top of the manual Bearer-PP header produces a malformed Authorization value that crashes JWTAuthenticationMiddleware\'s token.split(\' \') unpacking (terabase/middleware.py) before the view\'s own try/except ever runs.',
    parameters: [
      {
        name: 'task_id',
        type: 'string',
        required: true,
        description: 'Celery task ID returned by Generate Advanced Report (DOCX, Legacy) (in URL path)'
      }
    ],
    response: `// Pending / running
{ "status": "PENDING" }
{ "status": "STARTED" }

// Success
{
  "status": "SUCCESS",
  "result": {
    "url": "https://s3.amazonaws.com/presigned-url...",
    "file_name": "nextracker_advanced_report_....docx"
  }
}

// Failure (500)
{ "status": "FAILURE", "error": "..." }`
  },

  {
    id: 'nextracker-advanced-report',
    method: 'POST',
    path: '/tools/api/v1/terrain-pro/layouts/{layout_id}/reports/nextracker-advanced',
    title: 'Generate Nextracker Advanced Report',
    description: 'Queue generation of the Nextracker Advanced Report (template-based) for a layout. Resolves projectId/layoutId from the URL and email from the authenticated user server-side, instead of requiring them in the body like the legacy endpoint above — so it needs no legacy-prefix auth workaround and authenticates cleanly with the same client_credentials token /email-login returns. Queues the exact same generate_nextracker_docx_template_task Celery task as the legacy endpoint\'s ?version=template mode.',
    app: 'terrain-pro-api',
    reportDemoStep: 7,
    usedBy: ['report-demo'],
    usedByNote: 'Report Demo now calls this instead of the legacy Generate Advanced Report (DOCX, Legacy) endpoint above.',
    parameters: [
      {
        name: 'layout_id',
        type: 'integer',
        required: true,
        description: 'Layout ID (in URL path)'
      },
      {
        name: 'projectName',
        type: 'string',
        required: false,
        description: 'Display project name'
      },
      {
        name: 'location',
        type: 'string',
        required: false,
        description: 'Display location string'
      },
      {
        name: 'latitude',
        type: 'string',
        required: false,
        description: 'Display-formatted (DMS) latitude'
      },
      {
        name: 'longitude',
        type: 'string',
        required: false,
        description: 'Display-formatted (DMS) longitude'
      },
      {
        name: 'lat',
        type: 'number',
        required: false,
        description: 'Raw decimal latitude, used as a satellite-thumbnail fallback for the report\'s location map when the caller has no browser canvas to screenshot one'
      },
      {
        name: 'lon',
        type: 'number',
        required: false,
        description: 'Raw decimal longitude (see lat)'
      },
      {
        name: 'layoutName',
        type: 'string',
        required: false,
        description: 'Display layout name'
      },
      {
        name: 'coordinateSystem',
        type: 'string',
        required: false,
        description: 'Coordinate system name'
      },
      {
        name: 'epsgCode',
        type: 'string',
        required: false,
        description: 'EPSG code'
      },
      {
        name: 'filteredConfigIds',
        type: 'array',
        required: true,
        description: 'Configuration IDs selected in Step 2 (Select Figures)'
      },
      {
        name: 'selectedConfigDetails',
        type: 'array',
        required: true,
        description: 'Enriched configuration objects (from Get Layout Configurations (Legacy, proxied)), with earthWorkData converted to display units'
      },
      {
        name: 'units',
        type: 'string',
        required: false,
        description: 'Selected display unit system'
      },
      {
        name: 'sourceUnit',
        type: 'string',
        required: false,
        description: 'Layout\'s native storage unit system'
      },
      {
        name: 'arrayAreaUnit',
        type: 'string',
        required: false,
        description: '"ha" or "ac"'
      },
      {
        name: 'arrayArea',
        type: 'number',
        required: false,
        description: 'Total array area in arrayAreaUnit'
      },
      {
        name: 'bounds',
        type: 'object',
        required: false,
        description: 'Map bounds from Get/Create Layout Tiles & GeoJSON (proxied), or {} if not cached'
      },
      {
        name: 'PileRevealLegendRanges',
        type: 'array',
        required: false,
        description: 'Five {start, end} legend range buckets for the pile-reveal heatmap legend'
      },
      {
        name: 'socket_key',
        type: 'string',
        required: false,
        description: 'e.g. "report_demo_{layoutId}" — informational only, no WebSocket is used'
      },
      {
        name: 'revisionDetails',
        type: 'array',
        required: false,
        description: 'Revision history rows'
      },
      {
        name: 'inputFiles',
        type: 'array',
        required: false,
        description: 'Input file metadata rows'
      }
    ],
    response: `{
  "task_id": "celery-task-uuid",
  "status": "QUEUED"
}`
  },

  {
    id: 'nextracker-advanced-report-status',
    method: 'GET',
    path: '/tools/api/v1/terrain-pro/reports/nextracker-advanced/{task_id}',
    title: 'Check Nextracker Advanced Report Status',
    description: 'Poll the status of a queued Nextracker Advanced Report task started via Generate Nextracker Advanced Report above. Wraps celery.result.AsyncResult directly, same pattern as the legacy status endpoint. Once status is "SUCCESS", result.url IS the report file — a presigned S3 URL, fetch it with a fresh unauthenticated request (do not attach a Bearer header, S3 presigned URLs carry their own signature and an extra Authorization header can trigger SignatureDoesNotMatch).',
    app: 'terrain-pro-api',
    reportDemoStep: 7,
    usedBy: ['report-demo'],
    usedByNote: 'Polled every 5 seconds, up to 120 attempts (10-minute timeout). On SUCCESS, Report Demo auto-downloads the file via a synthetic anchor click and unlocks Step 7.',
    parameters: [
      {
        name: 'task_id',
        type: 'string',
        required: true,
        description: 'Celery task ID returned by Generate Nextracker Advanced Report (in URL path)'
      }
    ],
    response: `// Pending / running
{ "status": "PENDING" }
{ "status": "STARTED", "progress": 45 }

// Success
{
  "status": "SUCCESS",
  "result": {
    "url": "https://s3.amazonaws.com/presigned-url...",
    "file_key": "...",
    "file_name": "nextracker_advanced_report_....docx"
  }
}

// Failure (500)
{ "status": "FAILURE", "error": "..." }`
  }
];
